<template>
  <div :class="$style.carousel_wrapper">
    <div ref="carousel" :class="$style.carousel">
      <div :class="$style.container">
        <template v-for="(image, index) in slideImages" :key="image">
          <div v-if="isVideo(image)" :class="$style.slide">
            <video :class="$style.video" muted loop playsinline autoplay :src="image" />
          </div>

          <div v-else :class="$style.slide">
            <AtomsConsumerImage
              :class="$style.slide_image"
              :src="image"
              :width="windowWidth > 552 ? 552 : windowWidth"
              :height="windowWidth > 552 ? 735 : parseInt(((735 * windowWidth) / 552).toFixed())"
              :priority="index === 0 ? 'high' : 'auto'"
              :lazy="index !== 0"
              :quality="0.7"
            />
            <div v-if="isGiftWrappingAvailableImage(image) && giftWrapped" :class="$style.promotion_label">
              <span>※ 適用中のラッピングイメージ</span>
            </div>
            <div v-else-if="isGiftWrappingAvailableImage(image)" :class="$style.promotion_label">
              <span>※ ラッピングイメージ（別売）</span>
              <span :class="$style.tips">ラッピングご希望の場合は、ギフトラッピング設定をご確認ください</span>
            </div>
          </div>
        </template>
      </div>
    </div>
    <div :class="$style.buttons">
      <AtomsConsumerButton
        :class="[$style.button, $style.prev, { [$style.disabled]: carouselCanScroll.prev }]"
        @click="emblaApi?.scrollPrev()"
      />
      <AtomsConsumerButton
        :class="[$style.button, { [$style.disabled]: carouselCanScroll.next }]"
        @click="emblaApi?.scrollNext()"
      />
    </div>
    <div :class="[$style.img_num, { [$style.show]: imgNumShow }]">
      <span>{{ carouselSelectedSnap + 1 }} | {{ carouselSnapCount }}</span>
    </div>
    <div :class="$style.expansion_wrapper">
      <AtomsConsumerButton :class="$style.expansion" @click="$emit('expandClicked', slideImages[carouselSelectedSnap])">
        <AtomsConsumerImage src="/images/common/cart/plus.svg" />
      </AtomsConsumerButton>
    </div>
  </div>
</template>

<script setup lang="ts">
import emblaCarouselVue from "embla-carousel-vue"
import type { EmblaCarouselType } from "embla-carousel"
import { isVideo } from "@tential/ec-gql-schema/utils/functions/common"

defineProps<{
  slideImages: string[]
  /**
   * ギフトラッピング済みか（"適用中のラッピングイメージ"の表示制御を行う）
   */
  giftWrapped: boolean
  /**
   * ギフトラッピングの画像か（"ラッピングイメージ"の出しわけを行う）
   */
  isGiftWrappingAvailableImage: (path: string) => boolean
}>()

defineEmits<{
  (e: "expandClicked", url: string): void
}>()

const [carousel, emblaApi] = emblaCarouselVue({ slidesToScroll: 1 })

const carouselCanScroll = ref<{ prev: boolean; next: boolean }>({
  prev: true,
  next: true,
})
const carouselSnapCount = ref<number>(0)
const carouselSelectedSnap = ref<number>(0)
const imgNumShow = ref<boolean>(false)

/**
 * カルーセルのスクロールに応じてボタンの表示制御を行う
 */
const updateCarouselCanScroll = (value: EmblaCarouselType) => {
  carouselCanScroll.value = {
    prev: !value.canScrollPrev(),
    next: !value.canScrollNext(),
  }
}

/**
 * カルーセルのスクロールに応じて何番目のスライドが選択されているかを更新する
 */
const updateScrollSnapState = (value: EmblaCarouselType) => {
  carouselSnapCount.value = value.scrollSnapList().length
  carouselSelectedSnap.value = value.selectedScrollSnap()
  imgNumShow.value = true
}

watchEffect(() => {
  if (emblaApi.value) {
    updateCarouselCanScroll(emblaApi.value)
    updateScrollSnapState(emblaApi.value)
    emblaApi.value.on("slidesInView", () => {
      if (emblaApi.value) {
        updateCarouselCanScroll(emblaApi.value)
        updateScrollSnapState(emblaApi.value)
      }
    })
    emblaApi.value.on("scroll", () => {
      if (emblaApi.value) {
        updateCarouselCanScroll(emblaApi.value)
        updateScrollSnapState(emblaApi.value)
      }
    })
    emblaApi.value.on("init", () => {
      setTimeout(() => {
        imgNumShow.value = false
      }, 3000)
    })
    emblaApi.value.on("settle", () => {
      imgNumShow.value = false
    })
  }
})

const { width: windowWidth } = useWindowSize()
</script>

<style scoped module lang="scss">
.carousel_wrapper {
  position: relative;

  .carousel {
    overflow: hidden;

    .container {
      display: flex;
      gap: 0.25rem;

      .slide {
        display: flex;
        flex: 0 0 auto;
        width: 100%;
        cursor: pointer;
        border-radius: 0.375rem;

        .slide_image {
          aspect-ratio: 184 / 245;
          border-radius: 0.375rem;
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .video {
          width: 100%;
          align-self: center;
        }

        .promotion_label {
          width: 100%;
          position: absolute;
          bottom: 0.5rem;
          display: flex;
          flex-direction: column;
          align-items: flex-start;
          line-height: 1.5rem;
          font-size: $font-size-14;
          gap: 0.25rem;
          span {
            padding: 0 0.5rem;
          }
          .tips {
            font-size: $font-size-10;
          }
        }
      }
    }
  }

  .buttons {
    position: absolute;
    top: 50%;
    width: 100%;
    display: flex;
    justify-content: space-between;
    padding: 0;
    pointer-events: none;
    .button {
      display: block;
      width: 32px;
      height: 32px;
      background: url("/images/common/arrow/arrow-secondary-40.svg") no-repeat center $white;
      border-radius: 50%;
      z-index: 1;
      pointer-events: auto;
      transform: translate(45%, -50%);
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
      &.prev {
        transform: translate(-45%, -50%) scale(-1, 1);
      }
      &.disabled {
        opacity: 0;
      }
    }
  }

  .img_num {
    position: absolute;
    bottom: 20px;
    width: 100%;
    text-align: center;
    opacity: 0;
    transition: opacity 0.3s ease-out;
    span {
      background: rgba(0, 0, 0, 0.15);
      font-size: $font-size-12;
      color: $white;
      padding: 0.125rem 0.5rem;
      border-radius: 1em;
      display: inline-block;
      box-sizing: border-box;
      white-space: nowrap;
    }
    &.show {
      opacity: 1;
    }
  }

  .expansion_wrapper {
    position: relative;
    width: 100%;

    .expansion {
      position: absolute;
      bottom: 10px;
      right: 10px;
      width: 36px;
      height: 36px;
      background-color: $white;
      border-radius: 50%;
      display: flex;
      justify-content: center;
      box-shadow: 0px 0px 10px 0px rgba(0, 0, 0, 0.1);
    }
  }
}
</style>
